import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileCode2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export interface PromptPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptText: string;
  contextPrompt: string;
  glossaryPrompt?: string;
  guidancePrompt: string;
  projectName: string;
}

export function PromptPreviewSheet({
  open,
  onOpenChange,
  promptText,
  contextPrompt,
  glossaryPrompt = "",
  guidancePrompt,
  projectName,
}: PromptPreviewSheetProps) {
  const [copied, setCopied] = useState(false);
  const totalTokens = Math.ceil((promptText.length || 0) / 4);
  const contextTokens = Math.ceil((contextPrompt.length || 0) / 4);
  const glossaryTokens = Math.ceil((glossaryPrompt.length || 0) / 4);
  const guidanceTokens = Math.ceil((guidancePrompt.length || 0) / 4);

  function copyPrompt() {
    navigator.clipboard.writeText(promptText);
    setCopied(true);
    toast.success("Prompt context copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto flex flex-col p-6 gap-6"
      >
        <SheetHeader className="space-y-2">
          <div className="flex items-center gap-2">
            <FileCode2 className="h-5 w-5 text-primary" />
            <SheetTitle>Compiled runner prompt context</SheetTitle>
          </div>
          <SheetDescription className="text-xs leading-relaxed">
            This is the complete markdown context prepended to every walkthrough
            runner for {projectName}. It combines foundational project identity,
            governing authorities, key actors, domain vocabulary, and active
            learned guardrails.
          </SheetDescription>
        </SheetHeader>

        {/* Stats & Actions Bar */}
        <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/30 px-3.5 py-2.5">
          <div className="flex flex-wrap items-center gap-2.5 text-xs">
            <span className="font-semibold text-foreground">
              ~{totalTokens}{" "}
              <span className="text-muted-foreground font-normal">
                total tokens
              </span>
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              ~{contextTokens} core domain
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              ~{glossaryTokens} vocabulary
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              ~{guidanceTokens} guardrails
            </span>
          </div>
          <Button
            size="xs"
            variant="outline"
            className="gap-1.5 shrink-0"
            onClick={copyPrompt}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-primary" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy context
              </>
            )}
          </Button>
        </div>

        {/* Semantic Tabs: Structured Breakdown vs Raw Markdown */}
        <Tabs defaultValue="structured" className="flex flex-col">
          <TabsList className="grid w-full grid-cols-2 mb-4 shrink-0">
            <TabsTrigger value="structured">Structured sections</TabsTrigger>
            <TabsTrigger value="raw">Raw compiled prompt</TabsTrigger>
          </TabsList>

          <TabsContent value="structured" className="divide-y divide-border/60">
            {/* Section 1: Core Domain Context */}
            <div className=" pb-5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-primary" />
                  <span className="text-xs font-semibold text-foreground">
                    Core Domain Framing
                  </span>
                </div>
                <span className="text-xs tabular-nums text-muted-foreground font-mono">
                  ~{contextTokens} tokens
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Injected into every exploratory runner and scenario generator.
                Grounds AI personas in your product identity, governing
                authorities, stakeholder roles, and software boundaries.
              </p>
              <pre className="rounded-md bg-muted/35 p-3 font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words select-all">
                {contextPrompt || "No domain context defined yet."}
              </pre>
            </div>

            {/* Section 2: Domain Vocabulary (Glossary) */}
            <div className="py-5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-primary" />
                  <span className="text-xs font-semibold text-foreground">
                    Domain Vocabulary (Glossary)
                  </span>
                </div>
                <span className="text-xs tabular-nums text-muted-foreground font-mono">
                  ~{glossaryTokens} tokens
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Canonical domain terms, translations, definitions, and rejected
                synonyms that keep walkthrough runners aligned with your domain
                terminology.
              </p>
              {glossaryPrompt ? (
                <pre className="rounded-md bg-muted/35 p-3 font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words select-all">
                  {glossaryPrompt}
                </pre>
              ) : (
                <div className="rounded-md bg-muted/25 p-3 text-center text-xs text-muted-foreground">
                  No domain glossary terms defined yet. Add terms in the Domain
                  glossary section to lock in canonical terminology.
                </div>
              )}
            </div>

            {/* Section 3: Learned Guardrails & Triage Preferences */}
            <div className="pt-5 space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-full bg-amber-500" />
                  <span className="text-xs font-semibold text-foreground">
                    Learned Guardrails & Triage Preferences
                  </span>
                </div>
                <span className="text-xs tabular-nums text-muted-foreground font-mono">
                  ~{guidanceTokens} tokens
                </span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Negative instructions learned from past triage dismissals.
                Prevents repeat false alarms by explicitly instructing runners
                about out-of-scope behaviors.
              </p>
              {guidancePrompt ? (
                <pre className="rounded-md bg-muted/35 p-3 font-mono text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words select-all">
                  {guidancePrompt}
                </pre>
              ) : (
                <div className="rounded-md bg-muted/25 p-3 text-center text-xs text-muted-foreground">
                  No active learned guardrails. Dismissing findings with
                  &quot;Teach runner&quot; during triage will add negative rules
                  here.
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="raw" className="flex flex-col">
            <pre className="rounded-md bg-muted/35 p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words select-all">
              {promptText || "No context defined yet."}
            </pre>
          </TabsContent>
        </Tabs>

        <div className="rounded-md bg-muted/20 p-3 text-xs text-muted-foreground">
          <p className="leading-relaxed">
            <strong className="text-foreground">Why this matters:</strong> By
            injecting your real product identity, legislation, actors, domain
            vocabulary, and learned triage guardrails into every execution,
            walkthrough runners report genuine compliance and UX bugs instead of
            repeating false alarms.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
